import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import urls from "../../../../global/constants/UrlConstants";
import YhTable, { YhTableColumn } from "../../../../ui/YhTable";
import YhInput from "../../../../ui/YhInput";
import YhButton from "../../../../ui/YhButton";
import {
  isTruthy,
  openErrorNotification,
} from "../../../../helpers/methods";
import notifiers from "../../../../global/constants/NotificationConstants";
import { findUserLoanList } from "./AddRole/roleService";
import AddLoan from "./AddLoan";
import AddRole from "./AddRole/Role";

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const ViewLoansPage = () => {
  const history = useHistory();
  const { userId = "" } = useParams<{ userId: string }>();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [addLoanOpen, setAddLoanOpen] = useState(false);
  const [editLoanId, setEditLoanId] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  const loadLoans = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await findUserLoanList(userId);
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setRows(
        list.map((e: any) => ({
          id: e._id,
          _id: e._id,
          loan_acc: e.loan_acc || "—",
          loan_amt: e.loan_amt ?? "—",
          loan_emi: e.loan_emi ?? "—",
          start_from: e.start_from || "—",
          updated_amt: e.updated_amt ?? "—",
        }))
      );
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLoans();
  }, [userId]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.loan_acc, r.loan_amt, r.loan_emi, r.start_from, r.updated_amt]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, search]);

  const columns: YhTableColumn[] = [
    { field: "loan_acc", headerName: "Loan Account", flex: 1, minWidth: 140 },
    { field: "loan_amt", headerName: "Loan Amount", flex: 1, minWidth: 120 },
    { field: "loan_emi", headerName: "Loan EMI", flex: 1, minWidth: 120 },
    { field: "start_from", headerName: "Start From", flex: 1, minWidth: 130 },
    {
      field: "updated_amt",
      headerName: "Updated amount",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 100,
      renderCell: (params) => (
        <button
          type="button"
          className="text-sm font-semibold text-[#A02448] hover:underline"
          onClick={() => {
            setEditLoanId(String(params.row._id || params.row.id));
            setEditOpen(true);
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
            View Loan
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            View and manage loan details for this team member.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() => history.push(urls.archiveMember)}
          >
            Back
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            onClick={() => setAddLoanOpen(true)}
          >
            + Add Loan
          </YhButton>
        </div>
      </div>

      <div className="mb-4 flex shrink-0">
        <div className="w-full max-w-[320px]">
          <YhInput
            placeholder="Search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startAdornment={<SearchSvg />}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <YhTable
          columns={columns}
          rows={filteredRows}
          getRowId={(row) => row._id || row.id}
          loading={loading}
          emptyMessage="No loans found"
          className="h-full min-h-0"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={filteredRows.length}
          onPageChange={(p) =>
            setPaginationModel((prev) => ({ ...prev, page: p }))
          }
          onPageSizeChange={(s) =>
            setPaginationModel({ pageSize: s, page: 0 })
          }
        />
      </div>

      <AddLoan
        isDialogOpenLoan={addLoanOpen}
        handleCloseLoan={() => {
          setAddLoanOpen(false);
          loadLoans();
        }}
        userId={userId}
        status=""
      />

      <AddRole
        isDialogOpen={editOpen}
        handleCloseAddTeamMetaData={() => {
          setEditOpen(false);
          setEditLoanId("");
          loadLoans();
        }}
        idd={editLoanId}
        setIdd={setEditLoanId}
        userId={userId}
      />
    </div>
  );
};

export default ViewLoansPage;
